import React, { useEffect, useMemo, useState } from 'react';
import { useChat } from '@/contexts/ChatContext';
import { useAuth } from '@/components/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Search,
  MessageCircle,
  Users,
  X
} from 'lucide-react';

interface UserSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartDirectMessage: (userId: string, userName: string) => void;
}

export function UserSearchModal({ isOpen, onClose, onStartDirectMessage }: UserSearchModalProps) {
  const chatContext = useChat();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  // Get team members and loading state from context
  // Context now uses REST API, so it works in serverless environments
  const teamMembers = chatContext?.teamMembers || [];
  const isLoadingMembers = chatContext?.isLoadingMembers || false;

  // Trigger team members load when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (!user?.id) return;
    
    // If we don't have team members yet, trigger a load
    // This works even in serverless environments since it uses REST API
    if (teamMembers.length === 0 && !isLoadingMembers && chatContext) {
      console.log('[UserSearchModal] Triggering team members load...');
      // The context will auto-load on mount, but we can trigger a manual refresh if needed
      // For now, just wait for the auto-load to complete
    }
  }, [isOpen, user?.id, teamMembers.length, isLoadingMembers, chatContext]);

  // Debug: Log teamMembers when they change
  useEffect(() => {
    if (isOpen) {
      console.log('[UserSearchModal] Team members:', teamMembers);
      console.log('[UserSearchModal] Is loading:', isLoadingMembers);
    }
  }, [isOpen, teamMembers, isLoadingMembers]);

  // Filter org members locally — exclude self, match name or email
  const users = useMemo(() => {
    if (!teamMembers) {
      console.log('[UserSearchModal] No team members available');
      return [];
    }
    const filtered = teamMembers.filter(member => {
      if (member.id === user?.id) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        member.full_name?.toLowerCase().includes(q) ||
        member.email?.toLowerCase().includes(q)
      );
    });
    console.log('[UserSearchModal] Filtered users:', filtered.length);
    return filtered;
  }, [teamMembers, user?.id, searchQuery]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleUserSelect = (user: any) => {
    onStartDirectMessage(user.id, user.full_name || user.email);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Start Direct Message
          </DialogTitle>
          <DialogDescription>
            Search for a user in your organization to start a private conversation.
          </DialogDescription>
        </DialogHeader>

          <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-8"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

                  {/* Users List */}
                  <div className="h-64 overflow-y-auto">
            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Users className="h-12 w-12 text-muted-foreground mb-2" />
                <div className="text-sm text-muted-foreground">
                  {isLoadingMembers
                    ? 'Loading organization users...'
                    : searchQuery
                      ? 'No members match your search.'
                      : 'No team members found.'}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent cursor-pointer transition-colors"
                    onClick={() => handleUserSelect(member)}
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={member.avatar_url} />
                      <AvatarFallback>
                        {getInitials(member.full_name || member.email)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate min-w-0 flex-1">
                          {member.full_name || member.email}
                        </span>
                        {member.is_online && (
                          <Badge variant="secondary" className="text-xs">
                            Online
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {member.email}
                      </div>
                      {member.role && (
                        <div className="text-xs text-muted-foreground">
                          {member.role}
                        </div>
                      )}
                    </div>

                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                      </div>
                    )}
                  </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
